import React from 'react';
import { Link } from 'react-router-dom';
import {
  Card,
  CardContent,
  Input,
  Button,
  Badge,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@tonic/shared/components/ui';
import { Search, MoreVertical, Plus } from 'lucide-react';
import { DataTableWrapper } from '@/features/shared/components/data-display/DataTableWrapper';
import { mockRentals } from './mock-data';

// Extract all unique rental items from rentals
const getAllRentalItems = () => {
  const items = [];
  mockRentals.forEach(rental => {
    rental.items.forEach(item => {
      items.push({
        ...item,
        rentalId: rental.id,
        rentalStatus: rental.status,
        customer: rental.customer,
        startDate: rental.rentalPeriod.startDate,
        endDate: rental.rentalPeriod.endDate
      });
    });
  });
  return items;
};

const columns = [
  {
    accessorKey: 'sku',
    header: 'SKU',
    cell: ({ row }) => (
      <Link
        to={`/admin/re-commerce/rental/items/${row.original.productId}`}
        className="text-primary hover:underline"
      >
        {row.original.sku}
      </Link>
    )
  },
  {
    accessorKey: 'name',
    header: 'Name'
  },
  {
    accessorKey: 'rentalStatus',
    header: 'Status',
    cell: ({ row }) => (
      <Badge variant={getStatusVariant(row.original.rentalStatus)}>
        {row.original.rentalStatus}
      </Badge>
    )
  },
  {
    accessorKey: 'condition',
    header: 'Condition',
    cell: ({ row }) => (
      <Badge variant={getConditionVariant(row.original.condition.current || row.original.condition.checkout)}>
        {row.original.condition.current || row.original.condition.checkout}
      </Badge>
    )
  },
  {
    accessorKey: 'customer',
    header: 'Customer',
    cell: ({ row }) => (
      <div>
        <div className="font-medium">{row.original.customer.name}</div>
        <div className="text-sm text-muted-foreground">{row.original.customer.email}</div>
      </div>
    )
  },
  {
    accessorKey: 'pricing',
    header: 'Daily Rate',
    cell: ({ row }) => `$${row.original.pricing.dailyRate}`
  },
  {
    accessorKey: 'rentalPeriod',
    header: 'Rental Period',
    cell: ({ row }) => (
      <div className="text-sm">
        {new Date(row.original.startDate).toLocaleDateString()} -
        {new Date(row.original.endDate).toLocaleDateString()}
      </div>
    )
  },
  {
    id: 'actions',
    cell: ({ row }) => (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <span className="sr-only">Open menu</span>
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem>
            <Link to={`/admin/re-commerce/rental/items/${row.original.productId}`}>
              View Details
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem>Schedule Maintenance</DropdownMenuItem>
          <DropdownMenuItem>Update Status</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }
];

function getStatusVariant(status) {
  switch (status?.toLowerCase()) {
    case 'active':
      return 'default';
    case 'pending':
      return 'secondary';
    case 'completed':
      return 'outline';
    default:
      return 'outline';
  }
}

function getConditionVariant(condition) {
  switch (condition?.toLowerCase()) {
    case 'excellent':
      return 'default';
    case 'good':
      return 'secondary';
    case 'fair':
      return 'outline';
    case 'poor':
      return 'destructive';
    default:
      return 'outline';
  }
}

export function RentalItemsPage() {
  const [filters, setFilters] = React.useState({
    search: '',
    status: 'all',
    condition: 'all'
  });

  const items = React.useMemo(() => {
    let filteredItems = getAllRentalItems();

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filteredItems = filteredItems.filter(item =>
        item.name.toLowerCase().includes(searchLower) ||
        item.sku.toLowerCase().includes(searchLower) ||
        item.customer.name.toLowerCase().includes(searchLower)
      );
    }

    if (filters.status !== 'all') {
      filteredItems = filteredItems.filter(item =>
        item.rentalStatus.toLowerCase() === filters.status.toLowerCase()
      );
    }

    if (filters.condition !== 'all') {
      filteredItems = filteredItems.filter(item =>
        (item.condition.current || item.condition.checkout).toLowerCase() === filters.condition.toLowerCase()
      );
    }

    return filteredItems;
  }, [filters]);

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Rental Items</h1>
          <p className="text-sm text-muted-foreground">
            Manage items currently in the rental program
          </p>
        </div>
        <Button>
          <Plus className="w-4 h-4 mr-2" />
          Add Rental
        </Button>
      </div>

      <Card>
        <CardContent className="py-4">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
            <div className="relative flex-1 w-full md:max-w-sm">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-muted-foreground" />
              </div>
              <Input
                placeholder="Search items..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="pl-9 w-full"
              />
            </div>

            <div className="flex gap-4 items-center w-full md:w-auto">
              <Select
                value={filters.status}
                onValueChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>

              <Select
                value={filters.condition}
                onValueChange={(value) => setFilters(prev => ({ ...prev, condition: value }))}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by condition" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Conditions</SelectItem>
                  <SelectItem value="excellent">Excellent</SelectItem>
                  <SelectItem value="good">Good</SelectItem>
                  <SelectItem value="fair">Fair</SelectItem>
                  <SelectItem value="poor">Poor</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <DataTableWrapper
            columns={columns}
            data={items}
            emptyMessage="No rental items found"
            loadingMessage="Loading rental items..."
          />
        </CardContent>
      </Card>
    </div>
  );
} 
